import React, { useMemo, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, SendIcon, BanIcon, DownloadIcon } from 'lucide-react';
import { formatCurrency } from './pharmacyUtils';
import { PurchaseOrderDocument } from './PurchaseOrderDocument';
type DraftLine = {
  medicineId: string;
  quantity: string;
  unitCost: string;
};
const newLine = (): DraftLine => ({
  medicineId: '',
  quantity: '',
  unitCost: ''
});
export function PurchaseOrders() {
  const {
    suppliers,
    medicines,
    purchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    cancelPurchaseOrder,
    currentUser
  } = useData();
  const [showForm, setShowForm] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [documentOrderId, setDocumentOrderId] = useState<string | null>(null);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const orders = useMemo(() => [...purchaseOrders].sort((a, b) => new Date(b.dateReceived || b.createdAt).getTime() - new Date(a.dateReceived || a.createdAt).getTime()), [purchaseOrders]);
  const updateLine = (index: number, key: keyof DraftLine, value: string) => setLines(current => current.map((line, itemIndex) => itemIndex === index ? {
    ...line,
    [key]: value
  } : line));
  const cancelOrder = async (id: string) => {
    const reason = window.prompt('Enter the mandatory cancellation reason. The purchase order will remain in the audit register.');
    if (!reason) return;
    setCancellingId(id);
    const result = await cancelPurchaseOrder(id, reason);
    setCancellingId(null);
    if (!result.ok) setError(result.error || 'Unable to cancel purchase order.');
  };
  const save = (status: 'Draft' | 'Pending') => {
    const supplier = suppliers.find(item => item.id === supplierId);
    const parsedLines = lines.filter(line => line.medicineId && Number(line.quantity) > 0 && Number(line.unitCost) >= 0).map(line => {
      const medicine = medicines.find(item => item.id === line.medicineId)!;
      return {
        medicineId: medicine.id,
        medicineName: medicine.name,
        quantityOrdered: Number(line.quantity),
        quantityReceived: 0,
        unitCost: Number(line.unitCost)
      };
    });
    if (!supplier || !parsedLines.length) return setError('Select a supplier and add at least one valid medicine line.');
    createPurchaseOrder({
      supplierId: supplier.id,
      supplierName: supplier.name,
      status,
      orderedAt: new Date().toISOString(),
      expectedDate: expectedDate || undefined,
      notes: notes || undefined,
      lines: parsedLines,
      createdBy: currentUser?.id || '',
      kind: 'Planning'
    });
    setShowForm(false);
    setSupplierId('');
    setExpectedDate('');
    setNotes('');
    setLines([newLine()]);
    setError('');
  };
  return <div className="space-y-5" data-id="element-9530" data-mp="ydrnx2998ln2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-9531" data-mp="ycouamkjs53g">
        <div data-id="element-9532" data-mp="12dihteu2vhvt">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-9533" data-mp="12d0e5x5361dv">Purchase Orders</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-9534" data-mp="3j2nzwpe4e22w">
            Planning orders and completed delivery receipts share one live
            procurement register.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-9535" data-mp="77unuggnigc7">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-9536" data-mp="2ei34hf3etoq" /> New purchase order
        </Button>
      </div>
      {showForm && <section className="rounded-xl border border-teal-200 bg-teal-50/50 p-5" data-id="element-9537" data-mp="3tkb2v2il2q1a">
          <div className="mb-4 flex justify-between" data-id="element-9538" data-mp="3oovs4jfvha93">
            <div data-id="element-9539" data-mp="q66zc8v1c2ay">
              <h3 className="font-bold text-slate-900" data-id="element-9540" data-mp="q4m2udp5u9ag">New purchase order</h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-9541" data-mp="l62haeehazim">
                Use Receive New Stock to turn delivered items into an audited
                receipt.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-id="element-9542" data-mp="c65uyjfkacqq">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="mb-3 text-sm text-red-600" data-id="element-9543" data-mp="2wyhi4yazci2p">
              {error}
            </p>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-9544" data-mp="1gfef8droxm1j">
            <Select label="Supplier" value={supplierId} onChange={event => setSupplierId(event.target.value)} options={[{
          value: '',
          label: 'Select supplier'
        }, ...suppliers.filter(supplier => supplier.status === 'Active').map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }))]} data-id="element-9545" data-mp="1gdl6msxiorqw" />
            <Input label="Expected date" type="date" value={expectedDate} onChange={event => setExpectedDate(event.target.value)} data-id="element-9546" data-mp="293aade1m35w7" />
          </div>
          <div className="mt-4 overflow-x-auto" data-id="element-9547" data-mp="equ5agkjxxsx">
            <table className="min-w-[680px] w-full" data-id="element-9548" data-mp="1i0y7h83xoy3d">
              <thead data-id="element-9549" data-mp="1i1hrqvh23ykk">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-9550" data-mp="1i32l4hrmxbgu">
                  <th className="pb-2" data-id="element-9551" data-mp="1r1wf00dkznqp">Medicine</th>
                  <th className="pb-2" data-id="element-9552" data-mp="1r2pecga7nayt">Quantity</th>
                  <th className="pb-2" data-id="element-9553" data-mp="1m2t96dps1dho">Buying price</th>
                </tr>
              </thead>
              <tbody data-id="element-9554" data-mp="2103s284r1lnf">
                {lines.map((line, index) => <tr key={index} data-id="element-9555" data-mp="20y99h0gya5mi" data-mpi={index}>
                    <td className="min-w-64 pb-2 pr-3" data-id="element-9556" data-mp="1w18ziegzciq4">
                      <select aria-label="Medicine" value={line.medicineId} onChange={event => {
                  const medicine = medicines.find(item => item.id === event.target.value);
                  updateLine(index, 'medicineId', event.target.value);
                  if (!manager && medicine) updateLine(index, 'unitCost', String(medicine.buyingPrice));
                }} className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-9557" data-mp="1w0qv2uk5yy74">
                        <option value="" data-id="element-9558" data-mp="2tg0j61n30ubi">Select medicine</option>
                        {medicines.map(medicine => <option key={medicine.id} value={medicine.id} data-id="element-9559" data-mp="2kd95dv6qhmgp">
                            {medicine.name}
                          </option>)}
                      </select>
                    </td>
                    <td className="pb-2 pr-3" data-id="element-9560" data-mp="2aor83nxnxxht">
                      <input aria-label="Quantity" type="number" min="1" value={line.quantity} onChange={event => updateLine(index, 'quantity', event.target.value)} className="w-28 rounded-md border border-slate-300 p-2 text-sm" data-id="element-9561" data-mp="2ald46b9cpg89" />
                    </td>
                    <td className="pb-2" data-id="element-9562" data-mp="2750gtqmb5by8">
                      <input aria-label="Buying price" type="number" min="0" step="0.01" value={line.unitCost} disabled={!manager} onChange={event => updateLine(index, 'unitCost', event.target.value)} className="w-32 rounded-md border border-slate-300 p-2 text-sm" data-id="element-9563" data-mp="2280la9n33rld" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => setLines(current => [...current, newLine()])} className="mt-2 text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-9564" data-mp="1ah46h3hgrcvg">
            + Add medicine line
          </button>
          <label className="mt-4 block text-sm font-medium text-slate-700" data-id="element-9565" data-mp="3v4coq38vo1qv">
            Notes
            <textarea value={notes} onChange={event => setNotes(event.target.value)} rows={2} className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm" data-id="element-9566" data-mp="3ykzd62xct54" />
          </label>
          <div className="mt-4 flex justify-end gap-2" data-id="element-9567" data-mp="37cp3a0udxivz">
            <Button variant="outline" onClick={() => save('Draft')} data-id="element-9568" data-mp="3cdcscgz1zpnk">
              Save draft
            </Button>
            <Button onClick={() => save('Pending')} className="bg-teal-600 hover:bg-teal-700" data-id="element-9569" data-mp="3h6fnuune596e">
              <SendIcon className="mr-2 h-4 w-4" data-id="element-9570" data-mp="2ook3f4gumi2g" /> Submit order
            </Button>
          </div>
        </section>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-9571" data-mp="2xkbsrg8vof1t">
        <div className="overflow-x-auto" data-id="element-9572" data-mp="32idgieh912jm">
          <table className="min-w-[1050px] w-full divide-y divide-slate-100" data-id="element-9573" data-mp="32h25btlh4sih">
            <thead className="bg-slate-50" data-id="element-9574" data-mp="37jb10xpqe8oa">
              <tr data-id="element-9575" data-mp="37g579565qim8">
                {['Order / receipt', 'Supplier & invoice', 'Received', 'Total cost', 'Status', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-9576" data-mp="2jsx34qkgtfls">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-9577" data-mp="1w2b8q23dvtu9">
              {orders.map(order => <Fragment key={order.id} data-id="element-9578" data-mp="210csyqcjpz7b">
                  <tr data-id="element-9579" data-mp="i6jd2qro6z3w">
                    <td className="px-5 py-3.5" data-id="element-9580" data-mp="i72y4gcma3m5">
                      <p className="font-semibold text-slate-900" data-id="element-9581" data-mp="i4p0q0ugsfbf">
                        {order.orderNumber}
                      </p>
                      {order.serialNumber && <p className="mt-0.5 font-mono text-xs font-bold text-red-600" data-id="element-9582" data-mp="94a4i6fypft5">
                          {order.serialNumber}
                        </p>}
                      <p className="mt-1 text-xs text-slate-500" data-id="element-9583" data-mp="3vew762kov5pr">
                        {order.kind === 'Receipt' ? 'Delivery receipt' : 'Procurement plan'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-9584" data-mp="3hmgb5lq7g7yc">
                      <p data-id="element-9585" data-mp="3hk3l0laamra7">{order.supplierName}</p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-9586" data-mp="3clsb6ak00grz">
                        {order.invoiceNumber ? `Invoice ${order.invoiceNumber}` : 'Invoice recorded on receipt'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-9587" data-mp="32zr5111g6wiz">
                      {order.dateReceived ? <>
                          <p data-id="element-9588" data-mp="2p19l7eeny46c">
                            {new Date(order.dateReceived).toLocaleDateString()}
                          </p>
                          <p className="mt-1 text-xs text-slate-500" data-id="element-9589" data-mp="3gq49gtf4rt3k">
                            {order.receivedByName}
                          </p>
                        </> : order.expectedDate ? `Expected ${new Date(`${order.expectedDate}T00:00:00`).toLocaleDateString()}` : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700" data-id="element-9590" data-mp="3jax7cnf25ll">
                      {formatCurrency(order.totalCost ?? order.lines.reduce((sum, line) => sum + line.quantityOrdered * line.unitCost, 0))}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-9591" data-mp="22svqhv42fl4y">
                      <Status status={order.status} data-id="element-9592" data-mp="1xszkjqbt5jmr" />
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-9593" data-mp="1a1u53c9s4tcz">
                      <div className="flex flex-wrap justify-end gap-3" data-id="element-9594" data-mp="1a4ge7gcbjgxb">
                        {order.kind !== 'Receipt' && <button type="button" onClick={() => setDocumentOrderId(order.id)} className="inline-flex items-center whitespace-nowrap text-sm font-semibold text-blue-700 hover:text-blue-900" data-id="element-9595" data-mp="1k0a0hrfuaosa">
                            <DownloadIcon className="mr-1 h-4 w-4" data-id="element-9596" data-mp="2qhewg3zn7ltv" />
                            Download Purchase Order
                          </button>}
                        {order.status === 'Draft' && <button type="button" onClick={() => updatePurchaseOrder(order.id, {
                      status: 'Pending'
                    })} className="text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-9597" data-mp="14yv1cigy81be">
                            Submit
                          </button>}
                        {manager && order.kind !== 'Receipt' && !['Received', 'Cancelled'].includes(order.status) && <button type="button" disabled={cancellingId === order.id} onClick={() => cancelOrder(order.id)} className="inline-flex items-center text-sm font-semibold text-red-700 hover:text-red-900 disabled:opacity-50" data-id="element-9598" data-mp="21qf23vvxy6oo">
                              <BanIcon className="mr-1 h-4 w-4" data-id="element-9599" data-mp="26uh7l0kck1ra" />
                              {cancellingId === order.id ? 'Cancelling…' : 'Cancel'}
                            </button>}
                        <button type="button" onClick={() => setExpandedId(expandedId === order.id ? null : order.id)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-id="element-9600" data-mp="2uis4xtqu3jat">
                          Items{' '}
                          {expandedId === order.id ? <ChevronUpIcon className="h-4 w-4" data-id="element-9601" data-mp="1bl4882rks1tp" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-9602" data-mp="1gj5oyh1j3p20" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === order.id && <tr className="bg-slate-50/60" data-id="element-9603" data-mp="sue307ej82xo">
                      <td colSpan={6} className="px-5 py-4" data-id="element-9604" data-mp="ssjiuvb34eun">
                        <table className="min-w-full text-sm" data-id="element-9605" data-mp="xtqs6ysvlm3f">
                          <thead className="text-left text-xs uppercase tracking-wide text-slate-500" data-id="element-9606" data-mp="xqurx3tk2lbt">
                            <tr data-id="element-9607" data-mp="iyazoy7y1y95">
                              <th className="pb-2" data-id="element-9608" data-mp="nu888icyu0bd">Medicine</th>
                              <th className="pb-2" data-id="element-9609" data-mp="nwun7326ri3m">Batch</th>
                              <th className="pb-2" data-id="element-9610" data-mp="2dahe05w4wv5o">Quantity</th>
                              <th className="pb-2" data-id="element-9611" data-mp="2i9kinp87czd4">Buying price</th>
                              <th className="pb-2" data-id="element-9612" data-mp="2i6okq0wcuaoo">Received</th>
                            </tr>
                          </thead>
                          <tbody data-id="element-9613" data-mp="1n4epx4ip5ku">
                            {order.lines.map(line => <tr key={`${order.id}-${line.medicineId}-${line.batchNumber || ''}`} data-id="element-9614" data-mp="gb8scmopqqwr">
                                <td className="py-1.5 font-medium text-slate-800" data-id="element-9615" data-mp="bipd7ladnnpu">
                                  {line.medicineName}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-9616" data-mp="l9tsyn7vnclq">
                                  {line.batchNumber || '—'}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-9617" data-mp="uartgvj3x1zk">
                                  {line.quantityOrdered}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-9618" data-mp="313kk7ev2tkf2">
                                  {formatCurrency(line.buyingPrice ?? line.unitCost)}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-9619" data-mp="2f6zg1o7wrv7v">
                                  {line.quantityReceived}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </td>
                    </tr>}
                </Fragment>)}
              {!orders.length && <tr data-id="element-9620" data-mp="218ssb0wobblx">
                  <td colSpan={6} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-9621" data-mp="2189gm1xm9jgc">
                    No purchase orders or receipts yet.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
      {documentOrderId && <PurchaseOrderDocument orderId={documentOrderId} onClose={() => setDocumentOrderId(null)} data-id="element-9622" data-mp="3hp5bqtgxdjoc" />}
    </div>;
}
function Status({
  status
}: {
  status: string;
}) {
  const colors: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700',
    Pending: 'bg-blue-50 text-blue-700',
    'Partially Received': 'bg-amber-50 text-amber-700',
    Received: 'bg-emerald-50 text-emerald-700',
    Cancelled: 'bg-red-50 text-red-700'
  };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${colors[status] || 'bg-slate-100 text-slate-700'}`} data-id="element-9623" data-mp="md19m90cv7ck">
      {status}
    </span>;
}